/**
 * Protected Admin Route
 * Restricts access to admin-only pages
 */

import React from 'react';
import { Redirect } from 'wouter';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { AlertCircle, Crown } from 'lucide-react';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, loading, userRole } = useSupabaseAuth();

  console.log('[ProtectedAdminRoute] loading:', loading, 'userRole:', userRole, 'user:', user?.email);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Redirect to="/login" />;
  }

  // Check if user has admin role
  const isAdmin = userRole === 'admin';

  // If user doesn't have access, show access denied message
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-red-100 rounded-full p-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Access Denied</h1>
              <p className="text-red-600 font-semibold">Admin Access Required</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <p className="text-lg text-gray-700">
              This page is restricted to platform administrators only.
            </p>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Crown className="w-5 h-5 text-red-600" />
                Admin Panel
              </h3>
              <p className="text-gray-700">
                The Admin Panel allows platform administrators to manage user roles,
                grant creator access, and oversee platform operations.
              </p>
            </div>
          </div>

          <button
            onClick={() => window.history.back()}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User has admin access, render children
  return <>{children}</>;
};
